import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import useAppStore from '$appStore'
import { useMercuryFetch } from '$hooks'
import { appConfig } from '$utils'

/**
 * A single section of the application menu as returned by the Mercury
 * `/application-menu` endpoint. Only the fields the topbar's main module
 * navigation needs are typed here; the endpoint returns additional
 * `menuItems` / `subMenu` data used by the (later) sidenav work.
 */
export interface ApplicationMenuSection {
  /** The display label for the module, e.g. `Team`, `Stock`, `Admin`. */
  label: string
  /**
   * The hash link for the module's landing page, e.g. `#/team/staff-clocks`.
   * For the `Admin` module this is an absolute URL to the external admin site.
   */
  link: string
}

/**
 * The endpoint the module nav is built from. Also used as the React Query key
 * (`useMercuryFetch` keys on the URL), so the venue-change invalidation below
 * targets the exact same key.
 */
const APPLICATION_MENU_URL = '/application-menu'

/**
 * Bridge event AngularJS dispatches when the shared app context changes; a
 * `type` of 'company' means a venue switch cascade has completed.
 */
const APP_CONTEXT_CHANGE_EVENT = '@@loaded/appContextChange'

/**
 * Fetches the application menu for the active venue.
 *
 * We need to manually listen for changes in the active company because this
 * component is part of the topbar which renders outside the main React app
 * which is re-rendered if the app context changes.
 */
export default function useApplicationMenu() {
  const activeCompanyId = useAppStore((state) => state.activeCompany?.id)
  const isStandalone = appConfig.get<boolean>('standalone') ?? false
  const queryClient = useQueryClient()

  const query = useMercuryFetch<ApplicationMenuSection[]>(
    APPLICATION_MENU_URL,
    {
      enabled: isStandalone || Boolean(activeCompanyId),
    },
    // Swallow fetch errors into an empty (successful) result; the effect below
    // handles recovery on refocus/reconnect rather than surfacing an error.
    true
  )

  useEffect(() => {
    const handleAppContextChange = (ev: Event) => {
      const { type } = (ev as CustomEvent).detail || {}
      if (type === 'company') {
        queryClient.invalidateQueries({ queryKey: [APPLICATION_MENU_URL] })
      }
    }

    window.addEventListener(APP_CONTEXT_CHANGE_EVENT, handleAppContextChange)

    return () => {
      window.removeEventListener(
        APP_CONTEXT_CHANGE_EVENT,
        handleAppContextChange
      )
    }
  }, [queryClient])

  // Recovery for a failed menu fetch. `useMercuryFetch` disables
  // refetch-on-focus and (via the `swallowError` opt-in above) swallows fetch
  // errors into an empty (successful) result, so a transient failure would
  // otherwise leave the module nav
  // permanently empty until the next venue switch - the topbar stays mounted,
  // so the query never re-runs on its own. Once a fetch has SETTLED without a
  // menu (gated on !isFetching so we don't engage during the normal initial
  // load), retry when the tab is refocused or the network reconnects. The
  // listeners detach as soon as a fetch is in flight or a menu loads, so normal
  // operation isn't affected.
  const menuSettledEmpty = !query.isFetching && !query.data?.length
  useEffect(() => {
    if (!menuSettledEmpty) {
      return
    }

    const refetchMenu = () =>
      queryClient.invalidateQueries({ queryKey: [APPLICATION_MENU_URL] })

    window.addEventListener('online', refetchMenu)
    window.addEventListener('focus', refetchMenu)

    return () => {
      window.removeEventListener('online', refetchMenu)
      window.removeEventListener('focus', refetchMenu)
    }
  }, [menuSettledEmpty, queryClient])

  return query
}
